import { t } from "@lingui/core/macro";
import * as Square from "@square/web-sdk";
import { useCallback, useEffect, useState } from "react";

import { useGetSQUIPForWeb } from "@whoosh/features/src/Square/features/SquareCardEntry/hooks/useGetSQUIPForWeb";

import "./ApplePayButton.web.css";
import type { ApplePayButtonProps } from "./types";

const getError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

export const ApplePayButton = ({
  amount,
  countryCode = "US",
  currencyCode = "USD",
  isDisabled = false,
  label,
  locationId,
  onError,
  onNonce,
  onProcessingChange,
  onSuccess,
}: ApplePayButtonProps) => {
  const squareApplicationId = useGetSQUIPForWeb();
  const [applePay, setApplePay] = useState<Square.ApplePay | undefined>();
  const [isInitializing, setIsInitializing] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    let isActive = true;
    let applePayInstance: Square.ApplePay | undefined;

    const initialize = async () => {
      if (!locationId || amount <= 0) {
        if (isActive) {
          setIsInitializing(false);
        }
        return;
      }

      try {
        const payments = await Square.payments(squareApplicationId, locationId);
        if (!payments) {
          throw new Error("Square payments could not be initialized.");
        }
        const paymentRequest = payments.paymentRequest({
          countryCode,
          currencyCode,
          total: {
            amount: (amount / 100).toFixed(2),
            label,
          },
        });
        applePayInstance = await payments.applePay(paymentRequest);
        if (isActive) {
          setApplePay(applePayInstance);
        }
      } catch {
        // Apple Pay is optional; hide the button when the browser or merchant
        // configuration cannot initialize it.
      } finally {
        if (isActive) {
          setIsInitializing(false);
        }
      }
    };

    initialize();

    return () => {
      isActive = false;
      applePayInstance?.destroy();
      setApplePay(undefined);
    };
  }, [
    amount,
    countryCode,
    currencyCode,
    label,
    locationId,
    squareApplicationId,
  ]);

  const handlePress = useCallback(async () => {
    if (!applePay || isDisabled || isInitializing || isProcessing) {
      return;
    }

    setIsProcessing(true);
    onProcessingChange?.(true);

    try {
      const tokenResult = await applePay.tokenize();

      if (tokenResult.status !== "OK") {
        const message =
          tokenResult.errors?.map((error) => error.message).join(", ") ||
          t`Apple Pay authorization failed.`;
        throw new Error(message);
      }

      if (!tokenResult.token) {
        throw new Error(t`Apple Pay did not return a payment token.`);
      }

      await onNonce(tokenResult.token);
      onSuccess?.();
    } catch (error) {
      onError?.(getError(error));
    } finally {
      setIsProcessing(false);
      onProcessingChange?.(false);
    }
  }, [
    applePay,
    isDisabled,
    isInitializing,
    isProcessing,
    onError,
    onNonce,
    onProcessingChange,
    onSuccess,
  ]);

  if (!applePay) {
    return null;
  }

  return (
    <button
      aria-label={t`Pay with Apple Pay`}
      className="whoosh-apple-pay-button"
      disabled={isDisabled || isInitializing || isProcessing}
      onClick={handlePress}
      type="button"
    />
  );
};
